import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import emailSettingsApi from '@crema/services/apis/emailSettings';

const DeleteRecipientDialog = ({
  open,
  onClose,
  onSuccess,
  onError,
  recipient,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!recipient) return;

    try {
      setDeleting(true);
      const response = await emailSettingsApi.deleteRecipient(recipient.id);

      if (response.data.status === 200) {
        onSuccess(`Recipient "${recipient.name}" deleted successfully`);
      } else {
        onError(response.data.message || 'Failed to delete recipient');
      }
    } catch (error) {
      console.error('Error deleting recipient:', error);
      onError('Failed to delete recipient');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      onClose();
    }
  };

  if (!recipient) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center'>
          <WarningIcon color='warning' sx={{mr: 1}} />
          Delete Recipient
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity='warning' sx={{mb: 2}}>
          This action cannot be undone. The recipient will no longer receive
          daily reports.
        </Alert>

        <Box display='flex' alignItems='center' mb={2}>
          <PersonIcon sx={{mr: 1, color: 'text.secondary'}} />
          <Typography variant='body1' fontWeight='medium'>
            {recipient.name}
          </Typography>
        </Box>

        <Box display='flex' alignItems='center' mb={2}>
          <EmailIcon sx={{mr: 1, color: 'text.secondary'}} />
          <Typography variant='body1'>{recipient.email}</Typography>
        </Box>

        <Typography variant='body2' color='text.secondary'>
          Are you sure you want to delete this recipient? They will no longer
          receive daily reports.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant='contained'
          color='error'
          disabled={deleting}
        >
          {deleting ? (
            <>
              <CircularProgress size={16} sx={{mr: 1}} />
              Deleting...
            </>
          ) : (
            'Delete'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteRecipientDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  recipient: PropTypes.object.isRequired,
};

export default DeleteRecipientDialog;
